// src/components/workspace-switcher.tsx
import React from 'react';
import { Check } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthenticationDispatch } from '@/hooks/use-authentication';
import { toast } from 'sonner';
import { fetchWorkspaces, switchWorkspace } from '@/services/user';
import { useTranslation } from 'react-i18next';
const getInitials = (name) => {
    if (!name)
        return '—';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const initials = parts
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase();
    return initials || '—';
};
export const WorkspaceSwitcher = ({ workspace }) => {
    const { t } = useTranslation();
    const dispatch = useAuthenticationDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: workspaces = [], isPending } = useQuery({
        queryKey: ['my-workspaces'],
        queryFn: fetchWorkspaces,
        staleTime: 60_000,
    });
    const { mutate: doSwitch, isPending: isSwitching } = useMutation({
        mutationFn: switchWorkspace,
        onSuccess: async (data) => {
            dispatch({ type: 'SET_WORKSPACE', workspace: data.workspace });
            queryClient.invalidateQueries({});
            await navigate({ to: '/console' });
            toast.success(data.message);
        },
        onError: () => {
            toast.error(t('switch_workspace_error'));
        },
    });
    const currentName = workspace?.name ?? '—';
    const currentLogoUrl = workspace?.logoUrl ?? null;
    return (<DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto w-full p-2 justify-start group-data-[collapsible=icon]:p-1.5 group-data-[collapsible=icon]:justify-center" title={`${t('workspace')}: ${currentName}`} disabled={isPending || isSwitching}>
                    <div className="flex items-center gap-2 min-w-0 group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:justify-center">
                        <div className="h-9 w-9 grid place-items-center rounded-xl bg-primary/10 text-primary shrink-0 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7">
                            {currentLogoUrl ? (<img src={currentLogoUrl} alt={currentName} className="object-cover h-full w-full rounded-xl"/>) : (<span className="text-[10px] font-medium">{getInitials(currentName)}</span>)}
                        </div>

                        {/* Workspace label/name hidden in icon mode via the
            ancestor sidebar's `data-collapsible=icon` attr.
            CSS-based (not React state) so it works inside
            the DropdownMenu portal where useSidebar can be
            unreliable. */}
                        <div className="flex flex-col items-start min-w-0 group-data-[collapsible=icon]:hidden">
                            <span className="text-xs text-muted-foreground">{t('workspace')}</span>
                            <span className="text-sm font-medium leading-tight truncate max-w-45">{currentName}</span>
                        </div>
                    </div>
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="min-w-64">
                <DropdownMenuLabel>{t('switch_workspace')}</DropdownMenuLabel>
                <DropdownMenuSeparator />

                {workspaces.map((w) => {
            const active = w.publicId === workspace?.publicId;
            return (<DropdownMenuItem key={w.publicId} onClick={() => !active && doSwitch(w.publicId)} className="gap-2">
                            <Check className={cn('h-4 w-4', active ? 'opacity-100' : 'opacity-0')}/>

                            <Avatar className="h-6 w-6">
                                {w.logoUrl ? <AvatarImage src={w.logoUrl} alt={w.name}/> : null}
                                <AvatarFallback className="text-[10px] font-medium">
                                    {getInitials(w.name)}
                                </AvatarFallback>
                            </Avatar>

                            <span className="truncate">{w.name}</span>
                        </DropdownMenuItem>);
        })}
            </DropdownMenuContent>
        </DropdownMenu>);
};
WorkspaceSwitcher.displayName = 'WorkspaceSwitcher';
